import { Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Layout } from './Layout';
import { lazyImport } from './lazyImport';
import { LegacyCabinetRedirect } from './legacyPaths';
import { analyticsFlags } from '../features/listing-analytics/config';
import { proEnabled } from '../pro/flags';
import { CabinetFallback, ProFallback } from '../pro/ProFallback';
import { RouteFallback } from './components/RouteFallback';
import { RouteError } from './pages/RouteError';

import { GuestOnly } from './components/GuestOnly';
import { isNativeApp } from '../native/platform';
import { RequireAuth } from './components/RequireAuth';

import { Home } from './pages/Home';
import { AccountLayout } from './pages/AccountLayout';

const AboutUs = lazyImport(() =>
  import('./components/AboutUs').then((m) => ({ default: m.AboutUs })),
);
const PostGuideSection = lazyImport(() => import('./components/PostGuideSection'));
const SafeDeals = lazyImport(() => import('./components/SafeDeals'));
const AdminWatermark = lazyImport(() => import('./pages/AdminWatermark'));
const AdminPlans = lazyImport(() => import('./pages/AdminPlans'));
const TariffsPage = lazyImport(() => import('./pages/TariffsPage'));
const CabTariffs = lazyImport(() => import('../pro/cabinet/TariffsSection'));
function GenplanRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/turaq-kg-genplan-bishkek2050${search}`} replace />;
}

const GenplanPage = lazyImport(() =>
  import('./pages/GenplanPage').then((m) => ({ default: m.GenplanPage })),
);
const GenplanZonesPage = lazyImport(() =>
  import('./pages/GenplanZonesPage').then((m) => ({ default: m.GenplanZonesPage })),
);

const AgencyAgentsPage = lazyImport(() =>
  import('./pages/AgencyAgentsPage').then((m) => ({ default: m.AgencyAgentsPage })),
);
const SpecialistsPage = lazyImport(() =>
  import('./pages/SpecialistsPage').then((m) => ({ default: m.SpecialistsPage })),
);
const AgencyProfilePage = lazyImport(() =>
  import('./pages/AgencyProfilePage').then((m) => ({ default: m.AgencyProfilePage })),
);
const AgentProfilePage = lazyImport(() =>
  import('./pages/AgentProfilePage').then((m) => ({ default: m.AgentProfilePage })),
);
const CompanyProfilePage = lazyImport(() =>
  import('./pages/CompanyProfilePage').then((m) => ({ default: m.CompanyProfilePage })),
);
const CreateListing = lazyImport(() =>
  import('./pages/CreateProperty').then((m) => ({ default: m.CreateListing })),
);
const Dashboard = lazyImport(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const Favorites = lazyImport(() =>
  import('./pages/Favorites').then((m) => ({ default: m.Favorites })),
);
const ListingDetail = lazyImport(() =>
  import('./pages/ListingDetail').then((m) => ({ default: m.ListingDetail })),
);
const ExpertPage = lazyImport(() =>
  import('./pages/ExpertPage').then((m) => ({ default: m.ExpertPage })),
);
const Listings = lazyImport(() =>
  import('./pages/Listings').then((m) => ({ default: m.Listings })),
);
const Login = lazyImport(() =>
  import('./pages/Login').then((m) => ({ default: m.Login })),
);
const OwnerVerification = lazyImport(() =>
  import('./pages/OwnerVerification').then((m) => ({ default: m.OwnerVerification })),
);
const Privacy = lazyImport(() => import('./pages/Privacy'));
const Profile = lazyImport(() =>
  import('./pages/Profile').then((m) => ({ default: m.Profile })),
);
const Register = lazyImport(() =>
  import('./pages/Register').then((m) => ({ default: m.Register })),
);
const ForgotPassword = lazyImport(() =>
  import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
const EmailLinkSignIn = lazyImport(() =>
  import('./pages/EmailLinkSignIn').then((m) => ({ default: m.EmailLinkSignIn })),
);
const Terms = lazyImport(() => import('./pages/Terms'));
const PropertyMap = lazyImport(() =>
  import('./pages/PropertyMap').then((m) => ({ default: m.PropertyMap })),
);
const BlockedUsers = lazyImport(() =>
  import('./pages/BlockedUsers').then((m) => ({ default: m.BlockedUsers })),
);
const TopUpBalance = lazyImport(() =>
  import('./components/TopUpBalance').then((m) => ({ default: m.TopUpBalance })),
);
const TransactionHistory = lazyImport(() =>
  import('./components/TransactionHistory').then((m) => ({
    default: m.TransactionHistory,
  })),
);
const PromoteListing = lazyImport(() =>
  import('./pages/PromoteListing').then((m) => ({ default: m.PromoteListing })),
);

// Paying to promote a listing is a digital service consumed inside the app,
// which App Store Guideline 3.1.1 would require to go through In-App Purchase
// rather than an external payment provider. Rather than build IAP, the paid
// flows are simply absent from the native build — the website keeps them
// unchanged. Evaluated once at startup; Capacitor knows the platform before
// any route renders.
const paidRoutes = isNativeApp()
  ? []
  : [
      {
        path: 'listing/:id/promote',
        element: withFallback(<PromoteListing />),
      },
      { path: 'balance/top-up', element: withFallback(<TopUpBalance />) },
      {
        path: 'balance/transactions',
        element: withFallback(<TransactionHistory />),
      },
    ];
const OwnerListingsPage = lazyImport(() =>
  import('./components/ownerListings/OwnerListingsPage').then((m) => ({
    default: m.OwnerListingsPage,
  })),
);
const RealEstateHubPage = lazyImport(() =>
  import('./pages/RealEstateHubPage').then((m) => ({ default: m.RealEstateHubPage })),
);
const MessagesPage = lazyImport(() =>
  import('../chat/pages/MessagesPage').then((m) => ({ default: m.MessagesPage })),
);
const ListingAnalyticsPage = lazyImport(
  () => import('../features/listing-analytics/pages/ListingAnalyticsPage'),
);
const ProProfilePage = lazyImport(() =>
  import('../pro/profiles/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const LegacyProfileRedirect = lazyImport(() =>
  import('../pro/profiles/ProfilePage').then((m) => ({ default: m.LegacyProfileRedirect })),
);
const ComplexCatalogPage = lazyImport(() =>
  import('../pro/complexes/CatalogPage').then((m) => ({ default: m.CatalogPage })),
);
const ComplexPage = lazyImport(() =>
  import('../pro/complexes/ComplexPage').then((m) => ({ default: m.ComplexPage })),
);
const cabinet = () => import('../pro/cabinet');
const Cab = {
  Layout: lazyImport(() => cabinet().then((m) => ({ default: m.CabinetLayout }))),
  Home: lazyImport(() => cabinet().then((m) => ({ default: m.HomeSection }))),
  Listings: lazyImport(() => cabinet().then((m) => ({ default: m.ListingsSection }))),
  Profile: lazyImport(() => cabinet().then((m) => ({ default: m.ProfileSection }))),
  Leads: lazyImport(() => cabinet().then((m) => ({ default: m.LeadsSection }))),
  Clients: lazyImport(() => cabinet().then((m) => ({ default: m.ClientsSection }))),
  Calendar: lazyImport(() => cabinet().then((m) => ({ default: m.CalendarSection }))),
  Analytics: lazyImport(() => cabinet().then((m) => ({ default: m.AnalyticsSection }))),
  Reviews: lazyImport(() => cabinet().then((m) => ({ default: m.ReviewsSection }))),
  Marketing: lazyImport(() => cabinet().then((m) => ({ default: m.MarketingSection }))),
  Verification: lazyImport(() => cabinet().then((m) => ({ default: m.VerificationSection }))),
  Complexes: lazyImport(() => cabinet().then((m) => ({ default: m.ComplexesSection }))),
  ComplexEditor: lazyImport(() => cabinet().then((m) => ({ default: m.ComplexEditor }))),
  Account: lazyImport(() => cabinet().then((m) => ({ default: m.AccountSection }))),
  Agency: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyLayout }))),
  AgencyOverview: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyOverview }))),
  AgencyTeam: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyTeam }))),
  AgencyMember: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyMember }))),
  AgencyListings: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyListings }))),
  AgencyBase: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyBase }))),
  AgencyAccess: lazyImport(() => cabinet().then((m) => ({ default: m.AgencyAccess }))),
};
const ShortLinkPage = lazyImport(() =>
  import('../pro/pages/ShortLinkPage').then((m) => ({ default: m.ShortLinkPage })),
);
const OwnerReportPage = lazyImport(() =>
  import('../pro/pages/OwnerReportPage').then((m) => ({ default: m.OwnerReportPage })),
);
const SelectionPage = lazyImport(() =>
  import('../pro/cabinet/SelectionPage').then((m) => ({ default: m.SelectionPage })),
);
const ProSpecialistsPage = lazyImport(() =>
  import('../pro/profiles/SpecialistsPage').then((m) => ({ default: m.ProSpecialistsPage })),
);
const UiKitPage = lazyImport(() =>
  import('../pro/pages/UiKitPage').then((m) => ({ default: m.UiKitPage })),
);
const MyRequestsPage = lazyImport(() =>
  import('../pro/pages/MyRequestsPage').then((m) => ({ default: m.MyRequestsPage })),
);
const NotFound = lazyImport(() =>
  import('./pages/NotFound').then((m) => ({ default: m.NotFound })),
);

function withProFallback(element: ReactNode) {
  return <Suspense fallback={<ProFallback />}>{element}</Suspense>;
}

function withCabFallback(element: ReactNode) {
  return <Suspense fallback={<CabinetFallback />}>{element}</Suspense>;
}

function withFallback(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

// Warm the sign-in screens once the browser is idle, so tapping «Войти» /
// «Регистрация» opens instantly instead of waiting for a chunk download.
if (typeof window !== 'undefined') {
  const warm = () => {
    import('./pages/Login');
    import('./pages/Register');
  };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(warm, { timeout: 4000 });
  else window.setTimeout(warm, 3000);
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Home /> },
      { path: 'listings', element: withFallback(<Listings />) },
      { path: 'listing/:id', element: withFallback(<ListingDetail />) },
      // SEO-friendly canonical form ("/property/134-uyutnaya-kvartira") — see
      // helpers/listingPath.ts. The bare /listing/:id route above stays for
      // any link/bookmark already out there; both render the same page, the
      // slugged one is just what new links point to and what canonical says.
      { path: 'property/:id', element: withFallback(<ListingDetail />) },
      { path: 'expert', element: withFallback(<ExpertPage />) },
      // Business plans (GET /api/v2/plans): public, but a paid flow — web only (see paidRoutes).
      ...(isNativeApp() ? [] : [{ path: 'tariffs', element: withFallback(<TariffsPage />) }]),
      { path: 'specialists', element: proEnabled ? withProFallback(<ProSpecialistsPage />) : withFallback(<SpecialistsPage />) },
      { path: 'aboutus', element: withFallback(<AboutUs />) },
      { path: 'terms', element: withFallback(<Terms />) },
      // Советы по безопасной сделке — справка, открыта и без входа.
      { path: 'safedeals', element: withFallback(<SafeDeals />) },
      // «Правила размещения» из формы объявления — раздел про объявления в /terms.
      { path: 'rules', element: <Navigate to="/terms#ads" replace /> },
      { path: 'privacy', element: withFallback(<Privacy />) },
      // Both paths are live in old links / the app menu — one map page serves both.
      { path: 'kyrgyz-map', element: withFallback(<PropertyMap />) },
      { path: 'map-page', element: withFallback(<PropertyMap />) },
      // Removed from the menus in favour of /genplan; kept for existing links.
      { path: 'post-guide', element: withFallback(<PostGuideSection />) },
      { path: 'turaq-kg-genplan-bishkek2050', element: withFallback(<GenplanPage />) },
      // Indexable text pages for the map: all zones + one per zone, RU and KY.
      ...['', 'ky/'].flatMap((prefix) => [
        { path: `${prefix}turaq-kg-genplan-bishkek2050/zony`, element: withFallback(<GenplanZonesPage />) },
        { path: `${prefix}turaq-kg-genplan-bishkek2050/zona/:slug`, element: withFallback(<GenplanZonesPage />) },
      ]),
      // Short/old address — keeps the query (?lat=&lng=&z=) of shared links.
      { path: 'genplan', element: <GenplanRedirect /> },
      {
        path: 'nedvizhimost/:first?/:second?/:third?',
        element: withFallback(<RealEstateHubPage />),
      },
      // One hub route per language prefix (ky, kz, uz, tr, en, zh) — see
      // src/app/data/regions.ts SEO_LANGS for the prefix list.
      ...['ky', 'kz', 'uz', 'tr', 'en', 'zh'].map((prefix) => ({
        path: `${prefix}/nedvizhimost/:first?/:second?/:third?`,
        element: withFallback(<RealEstateHubPage />),
      })),

      // Owner report links are sent to listing owners — they work with or without the Pro flag.
      { path: 'owner-report/:token', element: withProFallback(<OwnerReportPage />) },
      // Collections are sent to clients — open with or without the Pro flag.
      { path: 'podborka', element: withProFallback(<SelectionPage />) },
      // turaq.kg/r/130 (realtor) or /r/<code> (marketing short link, docs/MARKETING.md).
      { path: 'r/:id', element: withFallback(<ShortLinkPage />) },

      // TURAQ Pro (docs/PRO_PLATFORM_PLAN.md) — only with VITE_PRO / ?pro=1.
      ...(proEnabled
        ? [
            { path: 'ui-kit', element: withFallback(<UiKitPage />) },
            // Public profiles (no login — indexable); the old addresses redirect here.
            { path: 'realtor/:slug', element: withProFallback(<ProProfilePage kind="realtor" />) },
            { path: 'agency/:slug', element: withProFallback(<ProProfilePage kind="agency" />) },
            { path: 'developer/:slug', element: withProFallback(<ProProfilePage kind="developer" />) },
            { path: 'novostroyki', element: withProFallback(<ComplexCatalogPage />) },
            { path: 'novostroyki/:slug', element: withProFallback(<ComplexPage />) },
            // Short links from the cabinet's marketing section: turaq.kg/r/130 etc.
            { path: 'a/:id', element: withFallback(<LegacyProfileRedirect kind="agency" />) },
            { path: 'd/:id', element: withFallback(<LegacyProfileRedirect kind="developer" />) },
            { path: 'agent-details/:id', element: withFallback(<LegacyProfileRedirect kind="realtor" />) },
            { path: 'agency-details/:id', element: withFallback(<LegacyProfileRedirect kind="agency" />) },
            { path: 'company-details/:id', element: withFallback(<LegacyProfileRedirect kind="developer" />) },
          ]
        : []),

      // With Pro the account lives in the cabinet; the old addresses forward there.
      {
        path: 'profile/blocked',
        element: proEnabled ? <Navigate to="/dashboard/blocked" replace /> : withFallback(<BlockedUsers />),
      },
      // Guests read Turaq.kg and use the assistant; chats with sellers ask to log in (ChatWindow).
      { path: 'messages', element: withFallback(<MessagesPage />) },
      { path: 'messages/:conversationId', element: withFallback(<MessagesPage />) },

      // Old cabinet addresses from notifications → /dashboard (see legacyPaths).
      { path: 'cabinet/*', element: <LegacyCabinetRedirect /> },

      {
        element: <RequireAuth />,
        children: [
          ...paidRoutes,
          // «Мои заявки»: a visitor's unit bookings and showing requests —
          // the backend's notifications to clients link to these two.
          { path: 'bookings', element: withProFallback(<MyRequestsPage />) },
          { path: 'showings', element: withProFallback(<MyRequestsPage />) },
          // TURAQ Pro: the new cabinet owns /dashboard. The previous cabinet is
          // gone; its old address (bookmarks) lands in the new one.
          ...(proEnabled
            ? [
                { path: 'dashboard/classic', element: <Navigate to="/dashboard" replace /> },
                {
                  path: 'dashboard',
                  element: withProFallback(<Cab.Layout />),
                  children: [
                    { index: true, element: withCabFallback(<Cab.Home />) },
                    { path: 'listings', element: withCabFallback(<Cab.Listings />) },
                    { path: 'profile', element: withCabFallback(<Cab.Profile />) },
                    { path: 'leads', element: withCabFallback(<Cab.Leads />) },
                    { path: 'clients', element: withCabFallback(<Cab.Clients />) },
                    { path: 'calendar', element: withCabFallback(<Cab.Calendar />) },
                    { path: 'analytics', element: withCabFallback(<Cab.Analytics />) },
                    { path: 'reviews', element: withCabFallback(<Cab.Reviews />) },
                    // The old team page lives on inside «Управление агентством».
                    { path: 'team', element: <Navigate to="/dashboard/agency/team" replace /> },
                    {
                      path: 'agency',
                      element: withCabFallback(<Cab.Agency />),
                      children: [
                        { index: true, element: withCabFallback(<Cab.AgencyOverview />) },
                        { path: 'team', element: withCabFallback(<Cab.AgencyTeam />) },
                        { path: 'team/:userId', element: withCabFallback(<Cab.AgencyMember />) },
                        { path: 'base', element: withCabFallback(<Cab.AgencyBase />) },
                        { path: 'listings', element: withCabFallback(<Cab.AgencyListings />) },
                        { path: 'access', element: withCabFallback(<Cab.AgencyAccess />) },
                      ],
                    },
                    { path: 'marketing', element: withCabFallback(<Cab.Marketing />) },
                    // Business plans inside the cabinet — a paid flow, web only.
                    ...(isNativeApp() ? [] : [{ path: 'tariffs', element: withCabFallback(<CabTariffs />) }]),
                    { path: 'verification', element: withCabFallback(<Cab.Verification />) },
                    { path: 'complexes', element: withCabFallback(<Cab.Complexes />) },
                    { path: 'complexes/:id', element: withCabFallback(<Cab.ComplexEditor />) },
                    // What the old /profile page held (balance, password, blocked…).
                    { path: 'account', element: withCabFallback(<Cab.Account />) },
                    { path: 'blocked', element: withCabFallback(<BlockedUsers />) },
                  ],
                },
              ]
            : [{ path: 'dashboard', element: withFallback(<Dashboard />) }]),
          ...(analyticsFlags.analyticsEnabled
            ? [{ path: 'dashboard/listing/:id/analytics', element: withFallback(<ListingAnalyticsPage />) }]
            : []),
          { path: 'create-listing', element: withFallback(<CreateListing />) },
          { path: 'favorites', element: withFallback(<Favorites />) },
          { path: 'owner-verification', element: withFallback(<OwnerVerification />) },
          { path: 'admin/watermark', element: withFallback(<AdminWatermark />) },
          { path: 'admin/plans', element: withFallback(<AdminPlans />) },
          // With TURAQ Pro on these addresses redirect to the public profiles above.
          ...(proEnabled
            ? []
            : [
                { path: 'company-details/:id', element: withFallback(<CompanyProfilePage />) },
                { path: 'agent-details/:id', element: withFallback(<AgentProfilePage />) },
                { path: 'agency-details/:id', element: withFallback(<AgencyProfilePage />) },
              ]),
          {
            path: 'agency-details/:id/specialists',
            element: withFallback(<AgencyAgentsPage />),
          },
          {
            element: proEnabled ? <Outlet /> : <AccountLayout />,
            children: [
              {
                path: 'profile',
                element: proEnabled ? <Navigate to="/dashboard/account" replace /> : withFallback(<Profile />),
              },
            ],
          },
          {
            path: 'company-details/:id/listings',
            element: withFallback(<OwnerListingsPage ownerType="COMPANY" />),
          },
          {
            path: 'agency-details/:id/listings',
            element: withFallback(<OwnerListingsPage ownerType="AGENCY" />),
          },
          {
            path: 'agent-details/:id/listings',
            element: withFallback(<OwnerListingsPage ownerType="REALTOR" />),
          },
        ],
      },

      // Catch-all — must stay last so it doesn't shadow real routes above.
      { path: '*', element: withFallback(<NotFound />) },
    ],
  },

  {
    path: '/login',
    errorElement: <RouteError />,
    element: withFallback(
      <GuestOnly>
        <Login />
      </GuestOnly>,
    ),
  },
  {
    path: '/email-link',
    errorElement: <RouteError />,
    element: withFallback(
      <GuestOnly>
        <EmailLinkSignIn />
      </GuestOnly>,
    ),
  },
  {
    path: '/forgot-password',
    errorElement: <RouteError />,
    element: withFallback(
      <GuestOnly>
        <ForgotPassword />
      </GuestOnly>,
    ),
  },
  {
    path: '/register',
    errorElement: <RouteError />,
    element: withFallback(
      <GuestOnly>
        <Register />
      </GuestOnly>,
    ),
  },
]);
